// ---------------------------------------------------------------------------
// regionsAlongRoute — which US states a route passes through, in order.
//
// Morgan: long-trip blurbs should call out the REGION the drive crosses, and
// explicitly note when it crosses state lines ("from Maine through New
// Hampshire into Massachusetts"). We derive this from the route's own polyline
// via point-in-polygon against a compact (≈5km-simplified) US state boundary
// set — no network, fully deterministic, unit-tested.
//
// The boundary data (src/data/usStates.json) is a simplified array of
// { name, polys } where polys is an array of polygons, each an array of rings
// ([outer, ...holes]) of [lng, lat] points. Simplified to ~5km, which is plenty
// for "which states does a multi-hour drive cross" — sampled interior points
// classify correctly even if a border is a few km off.
// ---------------------------------------------------------------------------

import type { LngLat } from "@/api/routing";
import statesData from "@/data/usStates.json";
import countriesData from "@/data/worldCountries.json";

interface RegionShape {
  name: string;
  polys: number[][][][]; // [polygon][ring][point][lng,lat]
}

const STATES = statesData as RegionShape[];
const COUNTRIES = countriesData as RegionShape[];

/** Country names that read better with an article or a friendlier form than the
 *  raw boundary-dataset label. Anything not listed is used verbatim. */
const COUNTRY_DISPLAY: Record<string, string> = {
  "United States of America": "the United States",
  "United Kingdom": "the United Kingdom",
  "Netherlands": "the Netherlands",
  "Czechia": "the Czech Republic",
  "Bosnia and Herz.": "Bosnia and Herzegovina",
  "North Macedonia": "North Macedonia",
  "Republic of Serbia": "Serbia",
};

/** Friendly display form of a country name (adds articles, expands abbrevs). */
export function displayCountry(name: string): string {
  return COUNTRY_DISPLAY[name] ?? name;
}

/** Ray-casting point-in-ring test. ring = [[lng,lat], ...]. */
function pointInRing(pt: LngLat, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect =
      (yi > pt[1]) !== (yj > pt[1]) &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** A point is in a polygon when it's inside the outer ring and outside every
 *  hole ring. */
function pointInPoly(pt: LngLat, poly: number[][][]): boolean {
  if (poly.length === 0 || !pointInRing(pt, poly[0])) return false;
  for (let h = 1; h < poly.length; h++) {
    if (pointInRing(pt, poly[h])) return false;
  }
  return true;
}

/** The region (from `regions`) containing this point, or null. */
function regionAt(pt: LngLat, regions: RegionShape[]): string | null {
  for (const s of regions) {
    for (const poly of s.polys) {
      if (pointInPoly(pt, poly)) return s.name;
    }
  }
  return null;
}

/** Ordered, distinct list of the regions a route passes through, first-
 *  appearance order. A region must contain at least `minSamples` sampled points
 *  to count — this drops a one-sample border blip while keeping every region a
 *  long drive genuinely spends time in. */
function regionsAlong(
  coords: LngLat[],
  regions: RegionShape[],
  opts: { maxSamples?: number; minSamples?: number } = {},
): string[] {
  if (!coords || coords.length === 0) return [];
  const maxSamples = opts.maxSamples ?? 80;
  const minSamples = opts.minSamples ?? 2;
  const stride = Math.max(1, Math.floor(coords.length / maxSamples));

  const order: string[] = [];
  const counts = new Map<string, number>();
  for (let i = 0; i < coords.length; i += stride) {
    const r = regionAt(coords[i], regions);
    if (!r) continue;
    counts.set(r, (counts.get(r) ?? 0) + 1);
    if (!order.includes(r)) order.push(r);
  }
  // Always test the final coordinate so the destination region is never missed
  // by the stride.
  const last = regionAt(coords[coords.length - 1], regions);
  if (last) {
    counts.set(last, (counts.get(last) ?? 0) + minSamples); // guarantee it counts
    if (!order.includes(last)) order.push(last);
  }
  return order.filter((r) => (counts.get(r) ?? 0) >= minSamples);
}

/** The US state containing this point, or null (ocean / outside the US). */
export function stateAt(pt: LngLat): string | null {
  return regionAt(pt, STATES);
}

/** The country containing this point, or null (ocean / unmapped). */
export function countryAt(pt: LngLat): string | null {
  return regionAt(pt, COUNTRIES);
}

/** Ordered, distinct US states a route crosses (first-appearance order). */
export function statesAlongRoute(
  coords: LngLat[],
  opts: { maxSamples?: number; minSamples?: number } = {},
): string[] {
  return regionsAlong(coords, STATES, opts);
}

/** Full state name → USPS abbreviation (the key our curated driving-roads files
 *  use). Covers the 50 states + DC. */
const STATE_ABBR: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC",
  Florida: "FL", Georgia: "GA", Hawaii: "HI", Idaho: "ID", Illinois: "IL",
  Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY", Louisiana: "LA",
  Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI",
  Minnesota: "MN", Mississippi: "MS", Missouri: "MO", Montana: "MT",
  Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
  "North Dakota": "ND", Ohio: "OH", Oklahoma: "OK", Oregon: "OR",
  Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT",
  Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
  Wisconsin: "WI", Wyoming: "WY",
};

/** USPS abbreviations for the states a route crosses (e.g. ["NY","CT"]).
 *  Used to lazy-load only the curated driving-road chunks a trip needs.
 *
 *  Callers can pass just [start, end]: we densify the path into evenly spaced
 *  samples first, so states crossed BETWEEN the endpoints (e.g. NJ on a NY→PA
 *  line) are caught. minSamples is 1 — even a briefly-crossed state should load
 *  its roads. Linear lng/lat interpolation is plenty at trip scale (borders are
 *  ~5km-simplified anyway). */
export function stateAbbrsAlongRoute(coords: LngLat[]): string[] {
  if (!coords || coords.length === 0) return [];
  const SAMPLES = 64;
  let path = coords;
  if (coords.length < SAMPLES) {
    path = [];
    for (let s = 0; s < coords.length - 1; s++) {
      const [ax, ay] = coords[s];
      const [bx, by] = coords[s + 1];
      const steps = Math.ceil(SAMPLES / Math.max(1, coords.length - 1));
      for (let t = 0; t < steps; t++) {
        const f = t / steps;
        path.push([ax + (bx - ax) * f, ay + (by - ay) * f]);
      }
    }
    path.push(coords[coords.length - 1]);
  }
  return statesAlongRoute(path, { minSamples: 1 })
    .map((name) => STATE_ABBR[name])
    .filter((ab): ab is string => Boolean(ab));
}

/** Ordered, distinct countries a route crosses (first-appearance order).
 *  Raw boundary-dataset names — pass through displayCountry() for rendering. */
export function countriesAlongRoute(
  coords: LngLat[],
  opts: { maxSamples?: number; minSamples?: number } = {},
): string[] {
  return regionsAlong(coords, COUNTRIES, opts);
}
